import { styled, Typography } from "@mui/material";
import { ChatMessage } from "chat/types";
import { ActionBar } from "desktopWindow/components/DesktopWindow.styled";
import * as React from "react";
import { useRef } from "react";
import { getInvertColor } from "utils/themeUtils";
import { useGameContext } from "../../context/GameContext";
import { WindowContent } from "../../desktopWindow/components/DesktopWindow";
import { useDesktopWindow } from "../../desktopWindow/context/DesktopWindowContext";
import { convertHexToRGBA } from "../../utils/commonUtils";
import { useChatRoom } from "../hooks/useChatRoom";
import { ChatInput } from "./ChatInput";
import { Row } from "./ChatMessage";

const ChatContainer = styled("div")({
	display: "flex",
	flexDirection: "column",
	height: "100%",
});

const ChatHeader = styled(ActionBar)({
	padding: "16px 8px",
	paddingLeft: 20,
});

const ChatChannelName = styled(Typography)(({ theme }) => ({
	fontWeight: "bold",
	color: convertHexToRGBA(getInvertColor(theme.palette.primary.main), 0.7),
	fontSize: "0.875rem",
}));

const ChatBox = React.memo(({ height, width }: Record<"height" | "width", number>) => {
	const { room } = useGameContext();
	const { isFocus } = useDesktopWindow();
	const { arrangedMessages, sendMessage, markAsRead, unreadMessages } = useChatRoom(room!.roomId, isFocus);
	const [toggleScroll, setToggleScroll] = React.useState<boolean>(false);

	const listContainerRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = React.useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	React.useEffect(() => {
		markAsRead();
	}, [markAsRead]);

	/**
	 * Scroll to bottom when new message is added
	 */
	React.useEffect(() => {
		scrollToBottom();
	}, [scrollToBottom, toggleScroll, arrangedMessages.length]);

	React.useEffect(() => {
		const listRef = listContainerRef?.current;
		const onScroll = () => {
			const scrollHeight = listRef!.scrollHeight;
			const scrollTop = listRef!.scrollTop;
			const clientHeight = listRef!.clientHeight;

			const scrollableHeight = scrollHeight - clientHeight;
			if (scrollTop >= scrollableHeight && unreadMessages.length > 0) {
				markAsRead();
			}
		};

		if (listRef) {
			listRef.addEventListener("scroll", onScroll);
			return function cleanup() {
				listRef.removeEventListener("scroll", onScroll);
			};
		}
	}, [markAsRead, unreadMessages.length]);

	return (
		<>
			<ChatHeader>
				<ChatChannelName>{room?.chatBotConfig.channelName}</ChatChannelName>
			</ChatHeader>

			<WindowContent>
				<ChatContainer>
					<div ref={listContainerRef} style={{ flex: 2, overflowY: "auto" }}>
						{arrangedMessages.map((message) => (
							<Row key={(message as ChatMessage)._id} message={message} style={{}} />
						))}
						<div ref={messagesEndRef} />
					</div>
					<div style={{ margin: 8 }}>
						<ChatInput onSend={sendMessage} toggleScroll={toggleScroll} setToggleScroll={setToggleScroll} />
					</div>
				</ChatContainer>
			</WindowContent>
		</>
	);
});

export default ChatBox;
